import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useReducedMotion,
  useMotionValueEvent,
} from "framer-motion";
import { ArrowDown } from "lucide-react";
import { HERO_STATES, HERO_VIDEO_SRC, HERO_PORTRAIT, LOGIN_HREF } from "@/data/content";

const Backdrop = ({ scale, y, videoRef, reduced }) => {
  if (HERO_VIDEO_SRC && !reduced) {
    return (
      <motion.video
        ref={videoRef}
        src={HERO_VIDEO_SRC}
        muted
        playsInline
        preload="auto"
        style={{ scale, y }}
        className="absolute inset-0 h-full w-full object-cover object-center"
      />
    );
  }
  return (
    <motion.div style={{ scale, y }} className="absolute inset-0">
      <img
        src={HERO_PORTRAIT}
        alt="Cagatay Turna"
        className="h-full w-full object-cover"
        style={{ objectPosition: "62% 22%" }}
      />
    </motion.div>
  );
};

const StateBlock = ({ state, style, isFirst }) => (
  <motion.div
    style={style}
    className="absolute inset-0 flex flex-col justify-end md:justify-center px-6 md:px-16 lg:px-24 pb-28 md:pb-0 max-w-[1100px]"
  >
    <p className="text-[11px] md:text-xs font-semibold tracking-[0.3em] uppercase text-gold mb-5">
      {state.overline}
    </p>
    <h1
      className={`font-semibold tracking-tight text-sand text-shadow-cinema whitespace-pre-line leading-[0.95] ${
        isFirst
          ? "text-[26vw] md:text-[18vw] lg:text-[15rem]"
          : "text-5xl sm:text-6xl lg:text-8xl"
      }`}
    >
      {state.title}
    </h1>
    <p className="mt-6 md:mt-8 max-w-xl text-base md:text-xl font-light leading-relaxed text-sand/80">
      {state.line}
    </p>
  </motion.div>
);

export const ScrollCinematicHero = () => {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const reduced = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start start", "end end"],
  });
  const smooth = useSpring(scrollYProgress, { stiffness: 140, damping: 30, mass: 0.3 });

  const scale = useTransform(smooth, [0, 1], [1.14, 1.34]);
  const bgY = useTransform(smooth, [0, 1], ["0%", "-7%"]);
  const veil = useTransform(smooth, [0, 1], [0.4, 0.6]);
  const barW = useTransform(smooth, [0, 1], ["0%", "100%"]);
  const hintO = useTransform(smooth, [0, 0.07], [1, 0]);

  // Sahne geçişlerinde "boşluk" oluşmaması için overlap'li opacity zincirleri.
  // o0 0→0.32 görünür, 0.20→0.35 sön. o1 0.25→0.40 doğ, 0.55→0.68 sön. o2 0.6→0.78 doğ, sona kadar dur.
  const o0 = useTransform(smooth, [0, 0.04, 0.22, 0.35], [1, 1, 1, 0]);
  const y0 = useTransform(smooth, [0, 0.35], [0, -60]);
  const o1 = useTransform(smooth, [0.25, 0.4, 0.58, 0.68], [0, 1, 1, 0]);
  const y1 = useTransform(smooth, [0.25, 0.68], [50, -50]);
  const o2 = useTransform(smooth, [0.60, 0.78, 1], [0, 1, 1]);
  const y2 = useTransform(smooth, [0.60, 1], [50, 0]);
  const cta = useTransform(smooth, [0.80, 0.90], [0, 1]);

  useMotionValueEvent(smooth, "change", (v) => {
    const vid = videoRef.current;
    if (vid && vid.duration && !reduced) {
      vid.currentTime = Math.min(vid.duration, Math.max(0, v * vid.duration));
    }
  });

  if (reduced) {
    const s = HERO_STATES[0];
    return (
      <section id="top" className="relative h-screen w-full overflow-hidden grain">
        <img src={HERO_PORTRAIT} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: "62% 22%" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/60 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-end md:justify-center px-6 md:px-16 lg:px-24 pb-28 md:pb-0">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-gold mb-5">{s.overline}</p>
          <h1 className="font-semibold tracking-tight text-sand text-[24vw] md:text-[14rem] leading-[0.9] whitespace-pre-line">{s.title}</h1>
          <p className="mt-6 max-w-xl text-lg text-sand/80">{s.line}</p>
        </div>
      </section>
    );
  }

  return (
    <section ref={wrapRef} id="top" className="relative h-[260vh]" data-testid="hero-scroll-section">
      <div className="sticky top-0 h-screen w-full overflow-hidden grain">
        <Backdrop scale={scale} y={bgY} videoRef={videoRef} reduced={reduced} />

        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/30" />
        <motion.div style={{ opacity: veil }} className="absolute inset-0 bg-ink" />
        <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 200px 60px rgba(6,10,20,0.85)" }} />

        <StateBlock state={HERO_STATES[0]} style={{ opacity: o0, y: y0 }} isFirst />
        <StateBlock state={HERO_STATES[1]} style={{ opacity: o1, y: y1 }} />
        <motion.div
          style={{ opacity: o2, y: y2 }}
          className="absolute inset-0 flex flex-col justify-end md:justify-center px-6 md:px-16 lg:px-24 pb-28 md:pb-0 max-w-[1100px]"
        >
          <p className="text-[11px] md:text-xs font-semibold tracking-[0.3em] uppercase text-gold mb-5">
            {HERO_STATES[2].overline}
          </p>
          <h1 className="font-semibold tracking-tight text-sand text-shadow-cinema whitespace-pre-line leading-[0.95] text-5xl sm:text-6xl lg:text-8xl">
            {HERO_STATES[2].title}
          </h1>
          <p className="mt-6 md:mt-8 max-w-xl text-base md:text-xl font-light leading-relaxed text-sand/80">
            {HERO_STATES[2].line}
          </p>
          <motion.a
            href={LOGIN_HREF}
            data-testid="hero-cta"
            style={{ opacity: cta }}
            className="group mt-10 inline-flex w-fit items-center gap-3 text-xs tracking-[0.2em] uppercase text-ink bg-gold rounded-full px-8 py-4 hover:bg-sand transition-colors duration-300"
          >
            Kurumsal İş Yönetimi
            <ArrowDown size={16} className="-rotate-90 group-hover:translate-x-1 transition-transform" />
          </motion.a>
        </motion.div>

        <motion.div
          style={{ opacity: hintO }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-mist"
        >
          <span className="text-[10px] tracking-[0.3em] uppercase">Kaydırın</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
            <ArrowDown size={16} />
          </motion.div>
        </motion.div>

        <div className="absolute bottom-0 left-0 h-[2px] w-full bg-white/10">
          <motion.div style={{ width: barW }} className="h-full bg-gold" />
        </div>
      </div>
    </section>
  );
};
